import { faToggleOff, faToggleOn } from "@fortawesome/free-solid-svg-icons";
import { faTimes } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import MaterialTable from "material-table";
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Button, Col, Modal, ModalBody, ModalHeader, Row } from "reactstrap";
import confirm from "reactstrap-confirm";
import { CurrentUserHasRole } from "../../api/auth";
import { apiUserFeatureDetails, apiUserFeatureSetIsEnabled, siteUserSendInvite, usersGetList, userUpdateSiteLockoutEnabled } from "../../api/users";
import { CurrentSiteIdGet, CurrentSiteProductType } from "../../api/userSettings";
import { features, isFeatureEnabled, productTypes, roles } from "../../config";
import { tableIcons } from "../../helpers/tableIcons";
import { RoleToDisplayRole } from "../../helpers/utils";
import local from "../../localization/strings";
import AddButton from "../common/AddButton";
import Loader from "../common/Loader";

const editIcon = () => <FontAwesomeIcon size="xs" icon="pencil-alt" />;

const UsersList = ({ editUser, addingUser }) => {
	const [users, setUser] = useState([]);
	const [featureResult, setFeatureResult] = useState();
	const departmentsFeatureEnabled = isFeatureEnabled(features.Departments);
	const [toggle, setToggle] = useState();
	const [working, setWorking] = useState(false);

	const isMaintainUsersAllowed = useCallback(
		(userId) => {
			if (Object.keys(featureResult.userEnabled).includes(userId)) {
				return !featureResult.userEnabled[userId];
			}
			return !featureResult.siteEnabled;
		},
		[featureResult],
	);

	const loadData = useCallback(async () => {
		var result = await usersGetList();

		if (CurrentUserHasRole(roles.Eltek) || CurrentUserHasRole(roles.Distributor)) {
			setFeatureResult(await apiUserFeatureDetails(CurrentSiteIdGet(), features.UserMaintenanceDisableForSiteAdmins));
		}

		result = result.filter((x) => x.role !== roles.Eltek && x.role !== roles.Distributor);
		result = result.filter((x) => !isFeatureEnabled(`DHSiteAdmin:${x.email}`));

		setUser(result);
		setWorking(false);
	}, []);

	const sendInvite = useCallback(
		async (userEmail) => {
			setUser([]);
			const sent = await siteUserSendInvite(CurrentSiteIdGet(), userEmail);
			if (sent) {
				toast.success(local.TF_SendInviteSuccessful);
			} else {
				toast.error(local.TF_SendInviteFailed);
			}
			await loadData();
		},
		[loadData],
	);

	const lockoutUser = useCallback(
		async (userId, lockedOutEnabled) => {
			setWorking(true);
			await userUpdateSiteLockoutEnabled(userId, CurrentSiteIdGet(), lockedOutEnabled);
			loadData();
		},
		[loadData],
	);

	const lockoutAllUsers = useCallback(async () => {
		setWorking(true);
		for (const user of users) {
			if (!user.siteLockoutEnabled) {
				await userUpdateSiteLockoutEnabled(user.id, CurrentSiteIdGet(), true);
			}
		}

		loadData();
	}, [loadData, users]);

	const UserEdit = ({ rowData }) => {
		if (!featureResult) {
			return null;
		}

		switch (rowData.role) {
			case roles.SiteAdmin:
				const allowed = isMaintainUsersAllowed(rowData.id);
				return <FontAwesomeIcon className="text-secondary cursor-pointer" icon={allowed ? faToggleOn : faToggleOff} size="2x" onClick={() => setToggle(rowData.id)} title={allowed ? local.TS_Yes : local.TS_No} />;
			case roles.DepartmentAdmin:
			case roles.SecondaryUser:
			case roles.DormantUser:
			default:
				return null;
		}
	};

	const UserStatus = ({ rowData }) => {
		if (rowData.lockedOut || rowData.siteLockoutEnabled) {
			return local.TF_Locked;
		}
		if (rowData.emailConfirmed) {
			return local.TF_Invite_Sent;
		}
		return (
			<Button color="primary" size="sm" className="btn text-nowrap" onClick={() => sendInvite(rowData.email)}>
				{local.TF_SendInvite}
			</Button>
		);
	};

	const columns = [
		{ field: "displayName", title: local.TS_Name, defaultSort: "asc" }, //
		{ field: "role", title: local.TS_Type, render: (rowData) => <span>{RoleToDisplayRole(rowData.role)}</span> },
		{ field: "departmentName", title: local.TS_Department, hidden: !departmentsFeatureEnabled },
		{ field: "email", title: local.TS_Email },
		{
			hidden: !(CurrentUserHasRole(roles.Eltek) || CurrentUserHasRole(roles.Distributor)),
			field: "siteLockoutEnabled",
			title: local.TS_Enabled,
			render: (rowData) => <>{CurrentUserHasRole(roles.Eltek) || CurrentUserHasRole(roles.Distributor) ? <FontAwesomeIcon className={`${rowData.siteLockoutEnabled ? "text-danger" : "text-secondary"} cursor-pointer`} icon={rowData.siteLockoutEnabled ? faToggleOff : faToggleOn} size="2x" onClick={() => lockoutUser(rowData.id, !rowData.siteLockoutEnabled)} title={rowData.siteLockoutEnabled ? local.TS_Enabled : local.TS_Disabled} /> : <>{rowData.siteLockoutEnabled ? <FontAwesomeIcon icon={faTimes} size="2x" className="text-danger" /> : ""}</>}</>,
		},
		{
			hidden: !(CurrentUserHasRole(roles.Eltek) || CurrentUserHasRole(roles.Distributor)),
			field: "role",
			title: local.TF_Can_Maintain_Users,
			render: (rowData) => <UserEdit rowData={rowData} />,
		},
		{
			hidden: !(CurrentUserHasRole(roles.Eltek) || CurrentUserHasRole(roles.Distributor)),
			field: "statuc",
			title: local.TF_Status,
			render: (rowData) => <UserStatus rowData={rowData} />,
		},
	];

	const addUserHandle = async () => {
		if (users.length >= 3 && CurrentSiteProductType() === productTypes.Lite) {
			await confirm({
				title: local.TS_Warning,
				message: local.TS_Max_Lite_Users,
				cancelText: null,
				confirmText: local.TS_Close,
			});
			return;
		}
		addingUser();
	};

	useEffect(() => {
		loadData();
	}, [loadData]);

	const toggleMaintainUsers = useCallback(
		async (id) => {
			await apiUserFeatureSetIsEnabled(CurrentSiteIdGet(), id, features.UserMaintenanceDisableForSiteAdmins, isMaintainUsersAllowed(id));
			await loadData();
			setToggle(undefined);
		},
		[isMaintainUsersAllowed, loadData],
	);

	return (
		<>
			<Modal isOpen={!!toggle} toggle={() => setToggle(undefined)} centered backdrop="static">
				<ModalHeader>{local.TF_Toggle_Maintain_Users.replace("|X|", users.find((x) => x.id === toggle)?.displayName || "")}</ModalHeader>
				<ModalBody>
					<Button size="sm" color="primary" onClick={() => toggleMaintainUsers(toggle)} className="ml-2">
						{local.TS_Yes}
					</Button>
					<Button size="sm" color="secondary" onClick={() => setToggle(undefined)} className="ml-2">
						{local.TS_No}
					</Button>
				</ModalBody>
			</Modal>
			<Row>
				<Col className="text-left">{CurrentUserHasRole(roles.SiteAdmin) ? <h5 className="mb-2">{local.TF_All_Users}</h5> : <h5 className="mb-2">{local.TF_Users_in_dept}</h5>}</Col>
				<Col className="text-right">
					{(CurrentUserHasRole(roles.Eltek) || CurrentUserHasRole(roles.Distributor)) && (
						<Button onClick={() => lockoutAllUsers()} color="falcon-danger" className={`mt-n2 mb-2 mr-2`}>
							<FontAwesomeIcon icon={"times"} /> {local.TF_Lockout_All_Users}
						</Button>
					)}
					<AddButton string={local.TF_Add_User} buttonClasses={"mt-n2 mb-2"} clickFunction={() => addUserHandle()} />
				</Col>
			</Row>
			<div className="mx-n3 mb-n3">
				{/* Searchable/sortable data table with all users listed for site admins, and just users from their department listed for dept admins */}
				{working ? (
					<Loader />
				) : (
					<MaterialTable
						columns={columns}
						data={users}
						title=""
						icons={tableIcons}
						options={{ exportButton: true, exportAllData: true, exportFileName: "Users", sorting: true, paging: true, pageSize: 50, pageSizeOptions: [50], emptyRowsWhenPaging: false, showEmptyDataSourceMessage: false, headerStyle: { backgroundColor: "#edf2f9", color: "#01579b" } }}
						actions={[
							{
								icon: editIcon,
								tooltip: "Modify",
								onClick: (_event, rowData) => editUser(rowData.id),
							},
						]}
					/>
				)}
			</div>
		</>
	);
};

export default UsersList;
